import React from 'react';
import {observer} from 'mobx-react-lite';
import intl from 'react-intl-universal';
import {useMobileNavController, useRootStore, useUserState} from "@stores/AppStateContext";

import type {MuiIconExternalProps} from '../../components/icons/MuiIcon';

import {SettingsIcon} from '../../components/icons/SettingsIcon';
import {PersonAdd} from '../../components/icons/PersonAdd';
import {PersonIcon} from "../../components/icons/PersonIcon";
import {KeyIcon} from "../../components/icons/KeyIcon";
import {createMuiIcon} from "../../components/icons/createMuiIcon";

const LocationIcon = createMuiIcon('location_on', 'LocationIcon');
import {
    SettingsSecondaryNav as SettingsSecondaryNavView,
    SettingsSecondaryNavSection
} from "../../components/SettingsSecondaryNav/SettingsSecondaryNav";

import {PAGES} from '@stores/ViewStore';
import {screenSizeProvider} from '@stores/ScreenSizeProvider';

export const SETTINGS_SECONDARY_NAV_WIDTH = 326;

type MenuItem = {
    id: string;
    title: string;
    Icon: React.ComponentType<MuiIconExternalProps>;
    page: typeof PAGES[keyof typeof PAGES];
}

const companySettings: MenuItem[] = [
    { id: 'stgGeneral', title: intl.get('settings-menu.general').d('General'), Icon: SettingsIcon, page: PAGES.SETTINGS_GENERAL },
    { id: 'stgMembers', title: intl.get('settings-menu.members').d('Users'), Icon: PersonAdd, page: PAGES.SETTINGS_MEMBERS },
    { id: 'stgLocations', title: intl.get('settings-menu.locations').d('Locations'), Icon: LocationIcon, page: PAGES.SETTINGS_LOCATIONS },
    { id: 'stgProfile', title: intl.get('profile.header').d('Profile'), Icon: PersonIcon, page: PAGES.SETTINGS_PROFILE },
];

const workspaceSettings: MenuItem[] = [
    { id: 'wsGeneral', title: intl.get('settings-menu.wsGeneral').d('Workspace General Settings'), Icon: SettingsIcon, page: PAGES.WORKSPACE_SETTINGS_GENERAL },
    { id: 'wsMembers', title: intl.get('settings-menu.wsMembers').d('Workspace Members'), Icon: PersonAdd, page: PAGES.WORKSPACE_MEMBERS },
    { id: 'stgAccesses', title: intl.get('settings-menu.accesses').d('Roles'), Icon: KeyIcon, page: PAGES.SETTINGS_ACCESSES },
];

export const isSettingsPage = (page: string) => [
    PAGES.SETTINGS_GENERAL,
    PAGES.SETTINGS_MEMBERS,
    PAGES.SETTINGS_LOCATIONS,
    PAGES.SETTINGS_ACCESSES,
    PAGES.SETTINGS_BILLING,
    PAGES.WORKSPACE_SETTINGS_GENERAL,
    PAGES.WORKSPACE_MEMBERS,
].includes(page as any);

export const SettingsSecondaryNav = observer(() => {
    const rootStore = useRootStore();
    const userState = useUserState();
    const mobileNav = useMobileNavController();
    const isMobile = screenSizeProvider.view !== 'desktop';
    if (isMobile && !mobileNav.showSettingsSecondary) return null;

    const root = rootStore;
    const active = root.view.page;

    const handleClick = (page: MenuItem['page']) => () => {
        root.view.openPageByName(page);
    }

    const mapItems = (items: MenuItem[]) => items.map((mi) => ({
        id: mi.id,
        title: mi.title,
        Icon: mi.Icon,
        isActive: active === mi.page,
        onClick: () => {
            handleClick(mi.page)();
            if (isMobile) mobileNav.openPage();
        },
    }));

    const sections: SettingsSecondaryNavSection[] = [
        {
            id: 'system',
            title: intl.get('settings-menu.system').d('System'),
            items: mapItems(companySettings),
        },
    ];

    if (userState?.availableWorkspaces.length) {
        sections.push({
            id: 'workspace',
            title: intl.get('Ws.context-menu.addMembers.WorkspaceSettings').d('Workspace Settings'),
            items: mapItems(workspaceSettings),
        });
    }

    return <SettingsSecondaryNavView sections={sections} isOverlay={isMobile}/>;
});
